import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import Navbar from "@/components/Navbar";
import CredentialSubmissionForm from "@/components/CredentialSubmissionForm";
import Footer from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  User,
  ShieldCheck,
  Stethoscope,
  Save,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Clock,
} from "lucide-react";
import { US_STATES } from "@/lib/us-states";

const SPECIALTIES = [
  "Endocrinology",
  "Internal Medicine",
  "Family Medicine",
  "Sports Medicine",
  "Anti-Aging / Regenerative Medicine",
  "Integrative Medicine",
  "Functional Medicine",
  "Obesity Medicine",
  "Other",
];

const ProviderProfile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Profile fields
  const [titlePrefix, setTitlePrefix] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");

  // Credential fields
  const [npiNumber, setNpiNumber] = useState("");
  const [licenseNumber, setLicenseNumber] = useState("");
  const [licenseState, setLicenseState] = useState("");
  const [licenseExpiry, setLicenseExpiry] = useState("");
  const [deaNumber, setDeaNumber] = useState("");
  const [deaExpiry, setDeaExpiry] = useState("");
  const [credentialStatus, setCredentialStatus] = useState("");
  const [rejectionReason, setRejectionReason] = useState<string | null>(null);

  // New fields
  const [specialty, setSpecialty] = useState("");
  const [bio, setBio] = useState("");

  const [hasCredentials, setHasCredentials] = useState(false);
  const [isResubmitting, setIsResubmitting] = useState(false);

  useEffect(() => {
    if (!user) return;
    loadData();
  }, [user]);

  const loadData = async () => {
    if (!user) return;
    setLoading(true);

    const [profileRes, credRes] = await Promise.all([
      supabase
        .from("profiles")
        .select("first_name, last_name, phone, specialty, bio, title_prefix")
        .eq("user_id", user.id)
        .single(),
      supabase
        .from("provider_credentials")
        .select("*")
        .eq("user_id", user.id)
        .single(),
    ]);

    if (profileRes.data) {
      setTitlePrefix((profileRes.data as any).title_prefix || "");
      setFirstName(profileRes.data.first_name || "");
      setLastName(profileRes.data.last_name || "");
      setPhone(profileRes.data.phone || "");
      setSpecialty((profileRes.data as any).specialty || "");
      setBio((profileRes.data as any).bio || "");
    }

    if (credRes.data) {
      setHasCredentials(true);
      setNpiNumber(credRes.data.npi_number);
      setLicenseNumber(credRes.data.medical_license_number);
      setLicenseState(credRes.data.license_state);
      setLicenseExpiry(credRes.data.license_expiry);
      setDeaNumber(credRes.data.dea_number || "");
      setDeaExpiry(credRes.data.dea_expiry || "");
      setCredentialStatus(credRes.data.credential_status);
      setRejectionReason(credRes.data.rejection_reason);
    }

    setLoading(false);
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);

    try {
      // Update profile (including specialty + bio)
      const { error: profileErr } = await supabase
        .from("profiles")
        .update({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          phone: phone.trim(),
          title_prefix: titlePrefix || null,
          specialty: specialty.trim() || null,
          bio: bio.trim() || null,
        } as any)
        .eq("user_id", user.id);

      if (profileErr) throw profileErr;

      toast({ title: "Profile updated", description: "Your changes have been saved." });
    } catch (err: any) {
      toast({ title: "Error saving", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const statusBadge = () => {
    switch (credentialStatus) {
      case "approved":
        return (
          <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
            <CheckCircle2 size={12} className="mr-1" /> Approved
          </Badge>
        );
      case "pending_review":
        return (
          <Badge variant="secondary" className="gap-1">
            <Clock size={12} /> Pending Review
          </Badge>
        );
      case "rejected":
        return (
          <Badge variant="destructive" className="gap-1">
            <AlertTriangle size={12} /> Rejected
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="gap-1">
            <Clock size={12} /> {credentialStatus || "Unknown"}
          </Badge>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="animate-spin text-primary" size={32} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container mx-auto px-4 pt-24 pb-16 max-w-3xl">
        <div className="mb-8">
          <h1 className="font-display text-3xl text-foreground">Provider Profile</h1>
          <p className="text-muted-foreground mt-1">
            Manage your professional information and credentials
          </p>
        </div>

        {/* Personal Info */}
        <Card className="border-border mb-6">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 font-display text-lg">
              <User size={18} className="text-primary" /> Personal Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="titlePrefix">Title</Label>
                <Select value={titlePrefix} onValueChange={setTitlePrefix}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select title" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Dr.">Dr.</SelectItem>
                    <SelectItem value="NP">NP</SelectItem>
                    <SelectItem value="PA-C">PA-C</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 123-4567"
              />
            </div>
          </CardContent>
        </Card>

        {/* Specialty & Bio */}
        <Card className="border-border mb-6">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 font-display text-lg">
              <Stethoscope size={18} className="text-primary" /> Professional Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="specialty">Specialty</Label>
              <Select value={specialty} onValueChange={setSpecialty}>
                <SelectTrigger>
                  <SelectValue placeholder="Select your specialty" />
                </SelectTrigger>
                <SelectContent>
                  {SPECIALTIES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="bio">Professional Bio</Label>
              <Textarea
                id="bio"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Brief overview of your background, training, and clinical focus..."
                rows={4}
                maxLength={1000}
              />
              <p className="text-xs text-muted-foreground text-right">
                {bio.length}/1000
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Credentials (read-only except specialty/bio) */}
        <Card className="border-border mb-6">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 font-display text-lg">
                <ShieldCheck size={18} className="text-primary" /> Credentials
              </CardTitle>
              {hasCredentials && statusBadge()}
            </div>
          </CardHeader>
          <CardContent>
            {!hasCredentials ? (
              <CredentialSubmissionForm userId={user!.id} onSubmitted={loadData} />
            ) : credentialStatus === "rejected" && isResubmitting ? (
              <div className="space-y-4">
                {rejectionReason && (
                  <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-4 text-sm text-destructive">
                    <p className="font-medium mb-1">Rejection Reason</p>
                    <p>{rejectionReason}</p>
                  </div>
                )}
                <CredentialSubmissionForm
                  userId={user!.id}
                  onSubmitted={() => { setIsResubmitting(false); loadData(); }}
                  isResubmission
                  initialValues={{
                    npiNumber,
                    licenseNumber,
                    licenseState,
                    licenseExpiry,
                    deaNumber,
                    deaExpiry,
                  }}
                />
                <Button variant="ghost" size="sm" onClick={() => setIsResubmitting(false)}>
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {rejectionReason && credentialStatus === "rejected" && (
                  <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-4 text-sm text-destructive">
                    <p className="font-medium mb-1">Rejection Reason</p>
                    <p>{rejectionReason}</p>
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">NPI Number</p>
                    <p className="text-sm text-foreground font-medium">{npiNumber}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">License Number</p>
                    <p className="text-sm text-foreground font-medium">{licenseNumber}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">License State</p>
                    <p className="text-sm text-foreground font-medium">{licenseState}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">License Expiry</p>
                    <p className="text-sm text-foreground font-medium">{licenseExpiry}</p>
                  </div>
                  {deaNumber && (
                    <>
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">DEA Number</p>
                        <p className="text-sm text-foreground font-medium">{deaNumber}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">DEA Expiry</p>
                        <p className="text-sm text-foreground font-medium">{deaExpiry || "—"}</p>
                      </div>
                    </>
                  )}
                </div>

                <Separator />
                {credentialStatus === "rejected" ? (
                  <Button variant="outline" size="sm" onClick={() => setIsResubmitting(true)}>
                    Resubmit Credentials
                  </Button>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Credential details are managed by your administrator. Contact
                    support if you need to update license or NPI information.
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Save */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Save Changes
          </Button>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ProviderProfile;
